import React, { useMemo, useState } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import EditableTable from "../../../../../../components/Tables/EditableTable";

const buildColumns = (rows) => {
  if (!rows?.length) {
    return [{ accessorKey: "id", header: "ID" }];
  }

  const baseColumns = [{ accessorKey: "id", header: "ID" }];
  const extraKeys = Object.keys(rows[0]).filter((key) => key !== "id");

  return [
    ...baseColumns,
    ...extraKeys.map((key) => ({
      id: key,
      accessorFn: (row) => row[key],
      header: key.replace(/_/g, " "),
    })),
  ];
};

export default function EditingTables({
  title = "Edit tables",
  tableOptions = [],
  dbTab,
  tableApi,
  initialTable,
  enableEdit = true,
}) {
  const [selectedTable, setSelectedTable] = useState(initialTable || "");
  const [columns, setColumns] = useState([{ accessorKey: "id", header: "ID" }]);

  const availableTables = useMemo(() => {
    if (tableOptions?.length) return tableOptions;
    return selectedTable ? [selectedTable] : [];
  }, [selectedTable, tableOptions]);

  const tableApiAdapter = useMemo(
    () => ({
      getItems: async () => {
        if (!selectedTable || !tableApi?.getItems) return [];
        const rows = (await tableApi.getItems(selectedTable, dbTab)) ?? [];
        setColumns(buildColumns(rows));
        return rows;
      },
      updateItem: async (item) => {
        if (!tableApi?.updateItem) return item;
        await tableApi.updateItem(item, selectedTable, dbTab);
        return item;
      },
    }),
    [dbTab, selectedTable, tableApi]
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Select table</InputLabel>
          <Select
            value={selectedTable}
            label="Select table"
            onChange={(event) => setSelectedTable(event.target.value)}
          >
            {availableTables.map((tableName) => (
              <MenuItem key={tableName} value={tableName}>
                {tableName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <EditableTable
        name={selectedTable || title}
        columns={columns}
        displayMode="modal"
        api={tableApiAdapter}
        enableEditing={enableEdit}
        enableCreate={false}
        initialData={{}}
        parentId={selectedTable}
        enableDelete={false}
        additionalProps={{
          layoutMode: "grid",
          defaultColumn: {
            minSize: 20,
            size: Math.max(60, Math.floor(900 / Math.max(columns.length, 1))),
          },
        }}
      />
    </Box>
  );
}
